/**
 * Customer Mapping Service - Cross-Platform ID Resolver
 *
 * Maps CIPP tenantId to other platform IDs (Sophos, Acronis, Autotask, ScalePad)
 * Uses Odin-MCP customer-mappings.json as the source of truth.
 *
 * @module customerMappingService
 */

import { callMCPTool } from './api/gateway';
import { findCustomerByCipp } from '../utils/findCustomerByCipp';

const ODIN_SERVER = 'Odin-MCP';

/**
 * Platform identifiers supported by the mapping service
 * @typedef {'cipp' | 'sophos' | 'acronis' | 'autotask' | 'scalepad' | 'datto'} Platform
 */

/**
 * Customer mapping object structure
 * @typedef {Object} CustomerMapping
 * @property {string} id - Internal customer ID
 * @property {string} displayName - Human-readable customer name
 * @property {Object} platformIds - Platform-specific IDs
 * @property {string|null} platformIds.cipp - CIPP/Azure AD Tenant GUID
 * @property {string|null} platformIds.sophos - Sophos Tenant ID
 * @property {string|null} platformIds.acronis - Acronis Tenant ID
 * @property {number|null} platformIds.autotask - Autotask Company ID
 * @property {string|null} platformIds.scalepad - ScalePad Site ID
 * @property {string|null} platformIds.datto - Datto Client Name
 * @property {string} [notes] - Optional notes about the customer
 * @property {string} createdAt - ISO timestamp of creation
 * @property {string} updatedAt - ISO timestamp of last update
 */

/**
 * Mapping lookup result
 * @typedef {Object} MappingResult
 * @property {boolean} success - Whether the lookup was successful
 * @property {CustomerMapping|null} customer - The customer mapping if found
 * @property {Object|null} platformIds - All platform IDs for the customer
 * @property {string|null} error - Error message if lookup failed
 */

// Cache for customer mappings
let mappingsCache = null;
let cacheTimestamp = null;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes cache TTL

/**
 * Fetch customer mappings from Odin-MCP
 * Uses caching to minimize MCP calls
 * @returns {Promise<CustomerMapping[]>} Array of customer mappings
 */
export const fetchCustomerMappings = async () => {
  const now = Date.now();

  // Return cached data if valid
  if (mappingsCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_TTL_MS) {
    return mappingsCache;
  }

  try {
    const result = await callMCPTool(ODIN_SERVER, 'list_customers');

    // Handle different response formats
    const customers = result?.customers || result?.data?.customers || result || [];

    // Update cache
    mappingsCache = Array.isArray(customers) ? customers : [];
    cacheTimestamp = now;

    return mappingsCache;
  } catch (error) {
    console.error('[CustomerMappingService] Failed to fetch mappings:', error);

    // Return cached data if available, even if stale
    if (mappingsCache) {
      console.warn('[CustomerMappingService] Using stale cache due to fetch error');
      return mappingsCache;
    }

    throw new Error(`Failed to fetch customer mappings: ${error.message}`);
  }
};

/**
 * Clear the customer mappings cache
 * Use when you know mappings have been updated
 */
export const clearMappingsCache = () => {
  mappingsCache = null;
  cacheTimestamp = null;
};

/**
 * Get customer mapping by CIPP tenant ID
 * Primary lookup function for cross-platform ID resolution
 *
 * @param {string} cippTenantId - The CIPP/Azure AD tenant GUID
 * @returns {Promise<MappingResult>} Mapping result with all platform IDs
 *
 * @example
 * const result = await getCustomerMapping('b736518a-5ad7-43bc-8a3d-dfa41dbc0cd0');
 * if (result.success) {
 *   console.log('Sophos ID:', result.platformIds.sophos);
 *   console.log('Autotask ID:', result.platformIds.autotask);
 * }
 */
export const getCustomerMapping = async (cippTenantId) => {
  if (!cippTenantId) {
    return {
      success: false,
      customer: null,
      platformIds: null,
      error: 'CIPP tenant ID is required',
    };
  }

  try {
    const mappings = await fetchCustomerMappings();

    // A CIPP tenant id (case-insensitive) or an odin: key for a customer without managed M365
    const customer = findCustomerByCipp(mappings, cippTenantId.trim());

    if (!customer) {
      return {
        success: false,
        customer: null,
        platformIds: null,
        error: `No customer found with CIPP tenant ID: ${cippTenantId}`,
      };
    }

    return {
      success: true,
      customer,
      platformIds: customer.platformIds,
      error: null,
    };
  } catch (error) {
    return {
      success: false,
      customer: null,
      platformIds: null,
      error: error.message,
    };
  }
};

/**
 * Get a specific platform ID for a customer by CIPP tenant ID
 *
 * @param {string} cippTenantId - The CIPP/Azure AD tenant GUID
 * @param {Platform} platform - Target platform ('sophos', 'acronis', 'autotask', 'scalepad', 'datto')
 * @returns {Promise<{success: boolean, platformId: string|number|null, error: string|null}>}
 *
 * @example
 * const sophos = await getPlatformId('b736518a-5ad7-43bc-8a3d-dfa41dbc0cd0', 'sophos');
 * if (sophos.success) {
 *   console.log('Sophos Customer ID:', sophos.platformId);
 * }
 */
export const getPlatformId = async (cippTenantId, platform) => {
  const validPlatforms = ['cipp', 'sophos', 'acronis', 'autotask', 'scalepad', 'datto'];

  if (!validPlatforms.includes(platform)) {
    return {
      success: false,
      platformId: null,
      error: `Invalid platform: ${platform}. Valid platforms: ${validPlatforms.join(', ')}`,
    };
  }

  const result = await getCustomerMapping(cippTenantId);

  if (!result.success) {
    return {
      success: false,
      platformId: null,
      error: result.error,
    };
  }

  const platformId = result.platformIds[platform];

  if (platformId === null || platformId === undefined) {
    return {
      success: false,
      platformId: null,
      error: `Platform '${platform}' not configured for customer: ${result.customer.displayName}`,
    };
  }

  return {
    success: true,
    platformId,
    error: null,
  };
};

/**
 * Get Sophos customer ID from CIPP tenant ID
 * @param {string} cippTenantId - The CIPP/Azure AD tenant GUID
 * @returns {Promise<string|null>} Sophos customer ID or null
 */
export const getSophosCustomerId = async (cippTenantId) => {
  const result = await getPlatformId(cippTenantId, 'sophos');
  return result.success ? result.platformId : null;
};

/**
 * Get Acronis tenant ID from CIPP tenant ID
 * @param {string} cippTenantId - The CIPP/Azure AD tenant GUID
 * @returns {Promise<string|null>} Acronis tenant ID or null
 */
export const getAcronisTenantId = async (cippTenantId) => {
  const result = await getPlatformId(cippTenantId, 'acronis');
  return result.success ? result.platformId : null;
};

/**
 * Get Autotask company ID from CIPP tenant ID
 * @param {string} cippTenantId - The CIPP/Azure AD tenant GUID
 * @returns {Promise<number|null>} Autotask company ID or null
 */
export const getAutotaskCompanyId = async (cippTenantId) => {
  const result = await getPlatformId(cippTenantId, 'autotask');
  return result.success ? result.platformId : null;
};

/**
 * Get ScalePad site ID from CIPP tenant ID
 * @param {string} cippTenantId - The CIPP/Azure AD tenant GUID
 * @returns {Promise<string|null>} ScalePad site ID or null
 */
export const getScalepadSiteId = async (cippTenantId) => {
  const result = await getPlatformId(cippTenantId, 'scalepad');
  return result.success ? result.platformId : null;
};

/**
 * Get Datto client name from CIPP tenant ID
 * @param {string} cippTenantId - The CIPP/Azure AD tenant GUID
 * @returns {Promise<string|null>} Datto client name or null
 */
export const getDattoClientName = async (cippTenantId) => {
  const result = await getPlatformId(cippTenantId, 'datto');
  return result.success ? result.platformId : null;
};

/**
 * Reverse lookup - find customer by any platform ID
 * Useful when you have a non-CIPP ID and need the full mapping
 *
 * @param {Platform} platform - Source platform
 * @param {string|number} platformId - Platform-specific ID
 * @returns {Promise<MappingResult>} Mapping result with all platform IDs
 *
 * @example
 * const result = await findCustomerByPlatformId('sophos', 'ceeaa39d-5122-4140-b906-041dda6afa64');
 * if (result.success) {
 *   console.log('CIPP Tenant ID:', result.platformIds.cipp);
 * }
 */
export const findCustomerByPlatformId = async (platform, platformId) => {
  const validPlatforms = ['cipp', 'sophos', 'acronis', 'autotask', 'scalepad', 'datto'];

  if (!validPlatforms.includes(platform)) {
    return {
      success: false,
      customer: null,
      platformIds: null,
      error: `Invalid platform: ${platform}. Valid platforms: ${validPlatforms.join(', ')}`,
    };
  }

  if (!platformId) {
    return {
      success: false,
      customer: null,
      platformIds: null,
      error: 'Platform ID is required',
    };
  }

  try {
    const mappings = await fetchCustomerMappings();

    // Normalize for comparison
    const normalizedId = String(platformId).toLowerCase().trim();

    const customer = mappings.find((c) => {
      const id = c.platformIds?.[platform];
      if (id === null || id === undefined) return false;
      return String(id).toLowerCase() === normalizedId;
    });

    if (!customer) {
      return {
        success: false,
        customer: null,
        platformIds: null,
        error: `No customer found with ${platform} ID: ${platformId}`,
      };
    }

    return {
      success: true,
      customer,
      platformIds: customer.platformIds,
      error: null,
    };
  } catch (error) {
    return {
      success: false,
      customer: null,
      platformIds: null,
      error: error.message,
    };
  }
};

/**
 * Get all customer mappings with optional filtering
 *
 * @param {Object} [options] - Filter options
 * @param {boolean} [options.onlyComplete] - Only return customers with all platforms configured
 * @param {Platform[]} [options.requiredPlatforms] - Only return customers with these platforms
 * @returns {Promise<CustomerMapping[]>} Array of customer mappings
 *
 * @example
 * // Get only customers with both Sophos and Acronis configured
 * const customers = await getAllCustomerMappings({
 *   requiredPlatforms: ['sophos', 'acronis']
 * });
 */
export const getAllCustomerMappings = async (options = {}) => {
  const { onlyComplete = false, requiredPlatforms = [] } = options;

  try {
    const mappings = await fetchCustomerMappings();

    return mappings.filter((customer) => {
      const platformIds = customer.platformIds || {};

      // Check for complete configuration if requested
      if (onlyComplete) {
        const allPlatforms = ['cipp', 'sophos', 'acronis', 'autotask', 'scalepad', 'datto'];
        const hasAll = allPlatforms.every(
          (p) => platformIds[p] !== null && platformIds[p] !== undefined
        );
        if (!hasAll) return false;
      }

      // Check for required platforms
      if (requiredPlatforms.length > 0) {
        const hasRequired = requiredPlatforms.every(
          (p) => platformIds[p] !== null && platformIds[p] !== undefined
        );
        if (!hasRequired) return false;
      }

      return true;
    });
  } catch (error) {
    console.error('[CustomerMappingService] Failed to get mappings:', error);
    throw error;
  }
};

/**
 * Get platform configuration status for a customer
 * Useful for understanding which platforms are configured
 *
 * @param {string} cippTenantId - The CIPP/Azure AD tenant GUID
 * @returns {Promise<{
 *   success: boolean,
 *   customer: string|null,
 *   configuredPlatforms: Platform[],
 *   missingPlatforms: Platform[],
 *   configurationPercent: number,
 *   error: string|null
 * }>}
 */
export const getCustomerPlatformStatus = async (cippTenantId) => {
  const result = await getCustomerMapping(cippTenantId);

  if (!result.success) {
    return {
      success: false,
      customer: null,
      configuredPlatforms: [],
      missingPlatforms: [],
      configurationPercent: 0,
      error: result.error,
    };
  }

  const allPlatforms = ['cipp', 'sophos', 'acronis', 'autotask', 'scalepad', 'datto'];
  const configuredPlatforms = [];
  const missingPlatforms = [];

  allPlatforms.forEach((platform) => {
    const id = result.platformIds[platform];
    if (id !== null && id !== undefined) {
      configuredPlatforms.push(platform);
    } else {
      missingPlatforms.push(platform);
    }
  });

  return {
    success: true,
    customer: result.customer.displayName,
    configuredPlatforms,
    missingPlatforms,
    configurationPercent: Math.round((configuredPlatforms.length / allPlatforms.length) * 100),
    error: null,
  };
};

// Default export with all functions
export default {
  // Core functions
  getCustomerMapping,
  getPlatformId,
  findCustomerByPlatformId,
  getAllCustomerMappings,

  // Platform-specific helpers
  getSophosCustomerId,
  getAcronisTenantId,
  getAutotaskCompanyId,
  getScalepadSiteId,
  getDattoClientName,

  // Utility functions
  getCustomerPlatformStatus,
  fetchCustomerMappings,
  clearMappingsCache,
};
